import React, { useState, useEffect, Fragment } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import {
 Row,
 Col,
 Card,
 Button,
 Form,
 Table,
 Badge,
 Spinner,
 Modal,
} from "react-bootstrap";
import swal from "sweetalert";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";

const statusCycle = ["", "P", "A", "L", "WO", "H"];
const AUTO_SUNDAY_WO = false;

const statusColors = {
 P: "success",
 A: "danger",
 L: "warning",
 WO: "secondary",
 H: "info",
};

const rolePriority = {
 admin: 1,
 manager: 2,
 engineer: 3,
 po: 4,
 billing: 5,
 client: 6,
 hr: 7,
 other: 99,
};

const AttendanceSheet = () => {
 const [workOrders, setWorkOrders] = useState([]);
 const [selectedWorkOrder, setSelectedWorkOrder] = usePersistentState("attendance_wo", "");
 const [selectedMonth, setSelectedMonth] = usePersistentState("attendance_month", new Date().getMonth());
 const [selectedYear, setSelectedYear] = usePersistentState("attendance_year", new Date().getFullYear());
 const [searchName, setSearchName] = usePersistentState("attendance_search", "");
 const [days, setDays] = useState(31);
 const [attendance, setAttendance] = useState({});
 const [employees, setEmployees] = useState([]);
 const [workers, setWorkers] = useState([]);
 const [workerTypes, setWorkerTypes] = useState([]);
 const [loading, setLoading] = useState(false);

 // Add/Edit Modal State
 const [showWorkerModal, setShowWorkerModal] = useState(false);
 const [isEdit, setIsEdit] = useState(false);
 const [workerForm, setWorkerForm] = useState({
 _id: null,
 name: "",
 mobile: "",
 skill: "",
 dailyRate: "",
 type: "",
 });

 // View Modal State
 const [showViewModal, setShowViewModal] = useState(false);
 const [selectedWorker, setSelectedWorker] = useState(null);

 const months = [
 "January",
 "February",
 "March",
 "April",
 "May",
 "June",
 "July",
 "August",
 "September",
 "October",
 "November",
 "December",
 ];

 /* =========================
 LOAD INITIAL DATA
 ========================= */
 useEffect(() => {
 loadWorkOrders();
 loadWorkerTypes();
 }, []);

 const loadWorkOrders = async () => {
 try {
 const res = await api.get("/work-orders");
 setWorkOrders(res.data.data || []);
 } catch {
 swal("Error", "Failed to load work orders", "error");
 }
 };

 const loadWorkerTypes = async () => {
 try {
 const res = await api.get("/worker-types?includeInactive=true");
 if (res.data.success) setWorkerTypes(res.data.data || []);
 } catch (err) {
 console.log("Failed to load worker types", err);
 }
 };

 /* =========================
 CALCULATE DAYS IN MONTH
 ========================= */
 useEffect(() => {
 setDays(new Date(selectedYear, selectedMonth + 1, 0).getDate());
 }, [selectedMonth, selectedYear]);

 /* =========================
 LOAD STAFF & WORKERS ON SELECT
 ========================= */
 useEffect(() => {
 if (selectedWorkOrder) {
 loadStaff();
 loadWorkers();
 }
 }, [selectedWorkOrder]);

 const loadStaff = async () => {
 try {
 setLoading(true);
 const res = await api.get(`/work-orders/${selectedWorkOrder}/employees`);
 let staff = (res.data.employees || []).map((e) => ({
 _id: e._id,
 name: e.name,
 role: e.roleName?.toLowerCase() || "other",
 type: "internal",
 }));

 staff = staff.sort(
 (a, b) => (rolePriority[a.role] || 99) - (rolePriority[b.role] || 99),
 );

 setEmployees(staff);
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Failed to load staff",
 "error",
 );
 }
 setLoading(false);
 };

 const loadWorkers = async () => {
 try {
 const res = await api.get(`/workers/by-workorder/${selectedWorkOrder}`);
 const list = (res.data.workers || []).map((w) => ({
 _id: w._id,
 name: w.name,
 mobile: w.mobile,
 skill: w.skill,
 dailyRate: w.dailyRate,
 typeId: w.type?._id,
 formattedType: w.type?.name || "Worker",
 isWorker: true,
 }));
 setWorkers(list);
 } catch {
 console.log("No workers found");
 }
 };

 /* =========================
 LOAD ATTENDANCE GRID
 ========================= */
 useEffect(() => {
 if (employees.length > 0 || workers.length > 0) loadAttendance();
 }, [employees, workers, selectedMonth, selectedYear]);

 const loadAttendance = async () => {
 setLoading(true);
 try {
 const combined = [...employees, ...workers];
 const res = await api.get(
 `/attendance/monthly/${selectedWorkOrder}/${selectedMonth}/${selectedYear}`,
 );
 const records = res.data.records || [];

 const grid = {};
 combined.forEach((emp) => {
 const rec = records.find((r) => r.employeeId == emp._id);
 if (rec) grid[emp._id] = rec.days;
 else grid[emp._id] = Array.from({ length: days }, () => "");
 });

 setAttendance(grid);
 } catch {
 swal("Error", "Failed to load attendance", "error");
 }
 setLoading(false);
 };

 /* =========================
 TOGGLE STATUS
 ========================= */
 const toggleStatus = (id, i) => {
 const curr = attendance[id][i] || "";
 const next =
 statusCycle[(statusCycle.indexOf(curr) + 1) % statusCycle.length];
 setAttendance((prev) => ({
 ...prev,
 [id]: prev[id].map((v, idx) => (idx === i ? next : v)),
 }));
 };

 /* =========================
 SAVE ATTENDANCE
 ========================= */
 const handleSave = async () => {
 setLoading(true);
 try {
 const combined = [...employees, ...workers];
 await Promise.all(
 combined.map((emp) =>
 api.post("/attendance/monthly/save", {
 workOrderId: selectedWorkOrder,
 employeeId: emp._id,
 employeeType: emp.isWorker ? "worker" : "internal",
 month: selectedMonth,
 year: selectedYear,
 days: attendance[emp._id],
 }),
 ),
 );

 swal("Saved!", "Attendance saved successfully", "success");
 } catch {
 swal("Error", "Failed to save attendance", "error");
 }
 setLoading(false);
 };

 /* =========================
 SAVE WORKER (ADD / EDIT)
 ========================= */
 const handleWorkerSave = async () => {
 try {
 if (isEdit) {
 await api.put(`/workers/${workerForm._id}`, {
 name: workerForm.name,
 mobile: workerForm.mobile,
 skill: workerForm.skill,
 dailyRate: workerForm.dailyRate,
 type: workerForm.type,
 });
 swal("Updated!", "Worker updated successfully", "success");
 } else {
 const { _id, ...payload } = workerForm; // Remove _id before create
 await api.post(`/workers`, {
 ...payload,
 workOrderId: selectedWorkOrder,
 });
 swal("Success!", "Worker added successfully", "success");
 }

 setShowWorkerModal(false);
 setWorkerForm({
 _id: null,
 name: "",
 mobile: "",
 skill: "",
 dailyRate: "",
 type: "",
 });
 loadWorkers();
 } catch (err) {
 swal(
 "Error",
 isEdit ? "Failed to update worker" : "Failed to add worker",
 "error",
 );
 }
 };

 /* =========================
 DELETE WORKER
 ========================= */
 const handleDeleteWorker = async (id) => {
 const confirm = await swal({
 title: "Are you sure?",
 text: "This worker will be permanently deleted!",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 });

 if (!confirm) return;

 try {
 await api.delete(`/workers/${id}`);
 swal("Deleted!", "Worker deleted.", "success");
 loadWorkers();
 } catch {
 swal("Error", "Failed to delete worker", "error");
 }
 };

 /* =========================
 FILTER worker + staff
 ========================= */
 const combinedList = [...employees, ...workers].filter((emp) =>
 emp.name?.toLowerCase().includes(searchName.toLowerCase()),
 );

 return (
 <Fragment>
 <PageTitle activeMenu="Attendance Sheet" motherMenu="HR" />

 <Row>
 <Col md={12}>
 <Card>
 <Card.Header>
 <Card.Title>Attendance Sheet</Card.Title>

 <div className="mt-2">
 <Badge bg="success" className="me-2">
 P = Present
 </Badge>
 <Badge bg="danger" className="me-2">
 A = Absent
 </Badge>
 <Badge bg="warning" className="me-2">
 L = Leave
 </Badge>
 <Badge bg="secondary" className="me-2">
 WO = Weekly Off
 </Badge>
 <Badge bg="info" className="me-2">
 H = Holiday
 </Badge>
 </div>
 </Card.Header>

 <Card.Body>
 {/* FILTERS */}
 <Row className="g-2 mb-3">
 <Col md={3}>
 <Form.Label>Work Order</Form.Label>
 <Form.Select
 size="sm"
 value={selectedWorkOrder}
 onChange={(e) => setSelectedWorkOrder(e.target.value)}
 >
 <option value="">Select</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo}{wo.label ? ` – ${wo.label}` : ""} - {wo.project.projectName}
 </option>
 ))}
 </Form.Select>
 </Col>

 <Col md={2}>
 <Form.Label>Month</Form.Label>
 <Form.Select
 size="sm"
 value={selectedMonth}
 onChange={(e) => setSelectedMonth(Number(e.target.value))}
 >
 {months.map((m, i) => (
 <option key={i} value={i}>
 {m}
 </option>
 ))}
 </Form.Select>
 </Col>

 <Col md={2}>
 <Form.Label>Year</Form.Label>
 <Form.Select
 size="sm"
 value={selectedYear}
 onChange={(e) => setSelectedYear(Number(e.target.value))}
 >
 {[2024, 2025, 2026, 2027, 2028].map((y) => (
 <option key={y} value={y}>
 {y}
 </option>
 ))}
 </Form.Select>
 </Col>

 <Col md={3}>
 <Form.Label>Search Name</Form.Label>
 <Form.Control
 size="sm"
 value={searchName}
 onChange={(e) => setSearchName(e.target.value)}
 placeholder="Search"
 />
 </Col>
 </Row>

 {/* ADD WORKER BUTTON */}
 {selectedWorkOrder && (
 <Button
 variant="primary"
 className="mb-2"
 onClick={() => {
 setIsEdit(false);
 setWorkerForm({
 _id: null,
 name: "",
 mobile: "",
 skill: "",
 dailyRate: "",
 type: "",
 });
 setShowWorkerModal(true);
 }}
 >
 + Add Worker
 </Button>
 )}

 {/* GRID */}
 {loading ? (
 <div className="text-center py-5">
 <Spinner animation="border" />
 </div>
 ) : (
 selectedWorkOrder && (
 <div className="attendance-scroll">
 <Table bordered size="sm">
 <thead>
 <tr>
 <th>Sr</th>
 <th>Name</th>
 <th>Type</th>
 {Array.from({ length: days }, (_, i) => (
 <th key={i} className="text-center">
 {i + 1}
 </th>
 ))}
 <th>Total P</th>
 <th>Action</th>
 </tr>
 </thead>

 <tbody>
 {combinedList.map((emp, index) => (
 <tr key={emp._id}>
 <td>{index + 1}</td>
 <td>{emp.name}</td>
 <td>
 {emp.isWorker ? emp.formattedType : emp.role}
 </td>

 {attendance[emp._id]?.map((st, i) => (
 <td
 key={i}
 onClick={() => toggleStatus(emp._id, i)}
 style={{ cursor: "pointer" }}
 >
 <Badge
 bg={statusColors[st] || "light"}
 text={st ? "light" : "dark"}
 >
 {st || ""}
 </Badge>
 </td>
 ))}

 <td className="fw-bold text-center">
 {
 attendance[emp._id]?.filter((x) => x === "P")
 .length
 }
 </td>

 <td>
 {emp.isWorker && (
 <>
 <Button
 size="sm"
 variant="info"
 className="me-1"
 onClick={() => {
 setIsEdit(true);
 setWorkerForm({
 _id: emp._id,
 name: emp.name,
 mobile: emp.mobile,
 skill: emp.skill,
 dailyRate: emp.dailyRate,
 type: emp.typeId,
 });
 setShowWorkerModal(true);
 }}
 >
 Edit
 </Button>

 <Button
 size="sm"
 variant="danger"
 className="me-1"
 onClick={() => handleDeleteWorker(emp._id)}
 >
 Delete
 </Button>

 <Button
 size="sm"
 variant="secondary"
 onClick={() => {
 setSelectedWorker(emp);
 setShowViewModal(true);
 }}
 >
 View
 </Button>
 </>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )
 )}

 {selectedWorkOrder && (
 <Button variant="success" className="mt-3" onClick={handleSave}>
 Save Attendance
 </Button>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* ADD/EDIT WORKER MODAL */}
 <Modal show={showWorkerModal} onHide={() => setShowWorkerModal(false)}>
 <Modal.Header closeButton>
 <Modal.Title>{isEdit ? "Edit Worker" : "Add Worker"}</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <Form.Group className="mb-2">
 <Form.Label>Name</Form.Label>
 <Form.Control
 value={workerForm.name}
 onChange={(e) =>
 setWorkerForm({ ...workerForm, name: e.target.value })
 }
 />
 </Form.Group>

 <Form.Group className="mb-2">
 <Form.Label>Mobile</Form.Label>
 <Form.Control
 value={workerForm.mobile}
 onChange={(e) =>
 setWorkerForm({ ...workerForm, mobile: e.target.value })
 }
 />
 </Form.Group>

 <Form.Group className="mb-2">
 <Form.Label>Skill</Form.Label>
 <Form.Control
 value={workerForm.skill}
 onChange={(e) =>
 setWorkerForm({ ...workerForm, skill: e.target.value })
 }
 />
 </Form.Group>

 <Form.Group className="mb-2">
 <Form.Label>Daily Rate</Form.Label>
 <Form.Control
 type="number"
 value={workerForm.dailyRate}
 onChange={(e) =>
 setWorkerForm({ ...workerForm, dailyRate: e.target.value })
 }
 />
 </Form.Group>

 <Form.Group className="mb-2">
 <Form.Label>Worker Type</Form.Label>
 <Form.Select
 value={workerForm.type}
 onChange={(e) =>
 setWorkerForm({ ...workerForm, type: e.target.value })
 }
 >
 <option value="">Select Type</option>
 {workerTypes.map((t) => (
 <option key={t._id} value={t._id}>
 {t.name}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowWorkerModal(false)}>
 Cancel
 </Button>
 <Button variant="success" onClick={handleWorkerSave}>
 {isEdit ? "Update" : "Add"}
 </Button>
 </Modal.Footer>
 </Modal>

 {/* VIEW WORKER MODAL */}
 <Modal show={showViewModal} onHide={() => setShowViewModal(false)}>
 <Modal.Header closeButton>
 <Modal.Title>Worker Details</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <p>
 <b>Name:</b> {selectedWorker?.name}
 </p>
 <p>
 <b>Mobile:</b> {selectedWorker?.mobile}
 </p>
 <p>
 <b>Skill:</b> {selectedWorker?.skill}
 </p>
 <p>
 <b>Daily Rate:</b> {selectedWorker?.dailyRate}
 </p>
 <p>
 <b>Type:</b> {selectedWorker?.formattedType}
 </p>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowViewModal(false)}>
 Close
 </Button>
 </Modal.Footer>
 </Modal>
 </Fragment>
 );
};

export default AttendanceSheet;
